import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, ReferenceLine,
} from 'recharts';
import { useEstimationStore } from '../../stores/estimationStore';
import { runMonteCarlo, getDefaultParams } from '../../utils/monteCarlo';
import type { MonteCarloResult, MonteCarloParam } from '../../utils/monteCarlo';
import { InfoTip } from '../shared/InfoTip';
import { fmt, fmtCHF } from '../../utils/format';
import { AXIS_STYLE, GRID_STYLE, TOOLTIP_STYLE } from '../charts/chartTheme';
import styles from './MonteCarloPage.module.css';

const BINS = 30;

function buildHistogram(values: number[], bins: number = BINS) {
  if (values.length === 0) return { buckets: [], min: 0, max: 0 };
  const sorted = [...values].sort((a, b) => a - b);
  const min = sorted[0]!;
  const max = sorted[sorted.length - 1]!;
  const range = max - min || 1;
  const step = range / bins;

  const buckets = new Array(bins).fill(0) as number[];
  for (const v of values) {
    const idx = Math.min(Math.floor((v - min) / step), bins - 1);
    buckets[idx] = (buckets[idx] ?? 0) + 1;
  }
  return { buckets, min, max };
}

interface HistogramProps {
  title: string;
  values: number[];
  format?: (v: number) => string;
  p10: number;
  p50: number;
  p90: number;
}

function Histogram({ title, values, format = fmt, p10, p50, p90 }: HistogramProps) {
  const { buckets, min, max } = useMemo(() => buildHistogram(values), [values]);
  const maxCount = Math.max(...buckets, 1);

  return (
    <div className={styles.histCard}>
      <div className={styles.histTitle}>{title}</div>
      <div className={styles.histCanvas}>
        {buckets.map((count, i) => {
          const w = 100 / buckets.length;
          const h = (count / maxCount) * 100;
          return (
            <div
              key={i}
              className={styles.histBar}
              style={{
                left: `${i * w}%`,
                width: `${w - 0.5}%`,
                height: `${h}%`,
              }}
              title={`${count} iterations`}
            />
          );
        })}
      </div>
      <div className={styles.histAxis}>
        <span>{format(min)}</span>
        <span>{format((min + max) / 2)}</span>
        <span>{format(max)}</span>
      </div>
      <div className={styles.histStats}>
        <span>P10: {format(p10)}</span>
        <span>P50: {format(p50)}</span>
        <span>P90: {format(p90)}</span>
      </div>
    </div>
  );
}

export function MonteCarloPage() {
  const { t } = useTranslation(['simulation', 'common']);
  const ha = useEstimationStore((s) => s.ha);
  const setHa = useEstimationStore((s) => s.setHa);
  const haZi = useEstimationStore((s) => s.haZi);
  const setHaZi = useEstimationStore((s) => s.setHaZi);
  const tenantRatios = useEstimationStore((s) => s.tenantRatios);
  const tenantBenchmarks = useEstimationStore((s) => s.tenantBenchmarks);

  const [iterations, setIterations] = useState(1000);
  const [params, setParams] = useState<MonteCarloParam[]>(() => getDefaultParams(tenantRatios));
  const [result, setResult] = useState<MonteCarloResult | null>(null);
  const [running, setRunning] = useState(false);

  const handleRun = useCallback(() => {
    setRunning(true);
    // Use requestAnimationFrame to allow UI update before computation
    requestAnimationFrame(() => {
      const res = runMonteCarlo(ha, haZi, params, tenantRatios, tenantBenchmarks, iterations);
      setResult(res);
      setRunning(false);
    });
  }, [ha, haZi, params, tenantRatios, tenantBenchmarks, iterations]);

  function updateParam(idx: number, field: keyof MonteCarloParam, value: number | string) {
    setParams((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx]!, [field]: value };
      return next;
    });
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h2>{t('simulation:title')}</h2>
          <div className={styles.headerSub}>
            {t('simulation:subtitle', { count: params.length })}
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className={styles.controls}>
        <div className={styles.controlGroup}>
          <span className={styles.controlLabel}>{t('simulation:iterations')}<InfoTip text={t('simulation:tooltips.iterations')} /></span>
          <input
            type="number"
            className={styles.controlInput}
            value={iterations}
            onChange={(e) => setIterations(Math.max(100, Math.min(10000, +e.target.value || 1000)))}
            min={100}
            max={10000}
            step={100}
          />
        </div>
        <div className={styles.controlGroup}>
          <span className={styles.controlLabel}>{t('simulation:haTotal', 'Surface totale (ha)')}<InfoTip text={t('simulation:tooltips.haTotal', 'Surface totale du périmètre du projet')} /></span>
          <input
            type="number"
            className={styles.controlInput}
            value={ha}
            onChange={(e) => setHa(Math.max(0, +e.target.value || 0))}
            min={0}
            max={200}
            step={1}
          />
        </div>
        <div className={styles.controlGroup}>
          <span className={styles.controlLabel}>{t('simulation:haZI')}<InfoTip text={t('simulation:tooltips.haZI')} /></span>
          <input
            type="number"
            className={styles.controlInput}
            value={haZi}
            onChange={(e) => setHaZi(Math.max(0, +e.target.value || 0))}
            min={0}
            max={100}
            step={1}
          />
        </div>
        <button className={styles.runBtn} onClick={handleRun} disabled={running}>
          {running ? t('simulation:simulating') : t('simulation:runIterations', { count: iterations })}
        </button>
      </div>

      {/* Parameter configuration */}
      <div className={styles.paramSection}>
        <h3 className={styles.sectionTitle}>{t('simulation:inputParameters')}<InfoTip text={t('simulation:tooltips.inputParameters')} /></h3>
        <div className={styles.paramGrid}>
          {params.map((p, i) => (
            <div key={p.key} className={styles.paramCard}>
              <div className={styles.paramHeader}>
                <span className={styles.paramName}>{p.label}</span>
                <span className={styles.paramDist}>{p.distribution}</span>
              </div>
              <div className={styles.paramRow}>
                <div className={styles.paramInputGroup}>
                  <input
                    className={styles.paramInput}
                    type="number"
                    value={p.min.toFixed(p.min < 1 ? 3 : 0)}
                    onChange={(e) => updateParam(i, 'min', +e.target.value)}
                  />
                  <span className={styles.paramInputLabel}>{t('simulation:min')}</span>
                </div>
                <div className={styles.paramInputGroup}>
                  <input
                    className={styles.paramInput}
                    type="number"
                    value={p.base.toFixed(p.base < 1 ? 3 : 0)}
                    onChange={(e) => updateParam(i, 'base', +e.target.value)}
                    style={{ borderColor: 'rgba(99,102,241,0.3)' }}
                  />
                  <span className={styles.paramInputLabel}>{t('simulation:base')}</span>
                </div>
                <div className={styles.paramInputGroup}>
                  <input
                    className={styles.paramInput}
                    type="number"
                    value={p.max.toFixed(p.max < 1 ? 3 : 0)}
                    onChange={(e) => updateParam(i, 'max', +e.target.value)}
                  />
                  <span className={styles.paramInputLabel}>{t('simulation:max')}</span>
                </div>
                <select
                  className={styles.distSelect}
                  value={p.distribution}
                  onChange={(e) => updateParam(i, 'distribution', e.target.value)}
                >
                  <option value="normal">{t('simulation:normal')}</option>
                  <option value="uniform">{t('simulation:uniform')}</option>
                  <option value="triangular">{t('simulation:triangular')}</option>
                </select>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Results */}
      {!result && !running && (
        <div className={styles.empty}>
          <p>{t('simulation:configureAndRun')}</p>
          <p className={styles.emptyHint}>
            {t('simulation:eachIteration')}
          </p>
        </div>
      )}

      {running && (
        <div className={styles.loading}>{t('simulation:runningIterations', { count: iterations })}</div>
      )}

      {result && !running && (
        <>
          {/* Confidence band summary */}
          <div className={styles.confidenceRow}>
            {[
              { key: 'cost_total' as const, label: t('simulation:totalCost'), format: fmtCHF },
              { key: 'logements' as const, label: t('simulation:logements'), format: fmt },
              { key: 'emplois' as const, label: t('simulation:emplois'), format: fmt },
              { key: 'sbp_total' as const, label: t('simulation:sbpTotal'), format: (v: number) => `${fmt(v)} m\u00B2` },
              { key: 'density' as const, label: t('simulation:density'), format: (v: number) => `${v.toFixed(1)} log/ha` },
              { key: 'classes' as const, label: t('simulation:classes'), format: (v: number) => v.toFixed(1) },
            ].map(({ key, label, format: formatFn }) => {
              const p = result.percentiles[key];
              const range = p.p90 - p.p10;
              const total = range || 1;
              const fillLeft = ((p.p25 - p.p10) / total) * 100;
              const fillWidth = ((p.p75 - p.p25) / total) * 100;
              return (
                <div key={key} className={styles.confidenceCard}>
                  <div className={styles.confidenceLabel}>{label}</div>
                  <div className={styles.confidenceMain}>{formatFn(p.p50)}</div>
                  <div className={styles.confidenceSub}>
                    {t('simulation:mean').toLowerCase()}: {formatFn(p.mean)} | {t('simulation:stdDev').toLowerCase()}: {formatFn(p.std)}
                  </div>
                  <div className={styles.confidenceRange}>
                    <span style={{ fontSize: 10 }}>{formatFn(p.p10)}</span>
                    <div className={styles.rangeBar}>
                      <div
                        className={styles.rangeFill}
                        style={{ left: `${fillLeft}%`, width: `${fillWidth}%` }}
                      />
                    </div>
                    <span style={{ fontSize: 10 }}>{formatFn(p.p90)}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Histograms */}
          <div className={styles.histSection}>
            <h3 className={styles.sectionTitle}>{t('simulation:distributionChart')}<InfoTip text={t('simulation:tooltips.distributionChart')} /></h3>
            <div className={styles.histGrid}>
              <Histogram
                title={t('simulation:totalCostCHF')}
                values={result.outputs.cost_total}
                format={fmtCHF}
                p10={result.percentiles.cost_total.p10}
                p50={result.percentiles.cost_total.p50}
                p90={result.percentiles.cost_total.p90}
              />
              <Histogram
                title={t('simulation:logements')}
                values={result.outputs.logements}
                p10={result.percentiles.logements.p10}
                p50={result.percentiles.logements.p50}
                p90={result.percentiles.logements.p90}
              />
              <Histogram
                title={t('simulation:emplois')}
                values={result.outputs.emplois}
                p10={result.percentiles.emplois.p10}
                p50={result.percentiles.emplois.p50}
                p90={result.percentiles.emplois.p90}
              />
              <Histogram
                title={t('simulation:sbpTotalM2')}
                values={result.outputs.sbp_total}
                format={(v) => `${fmt(v)} m\u00B2`}
                p10={result.percentiles.sbp_total.p10}
                p50={result.percentiles.sbp_total.p50}
                p90={result.percentiles.sbp_total.p90}
              />
            </div>
          </div>

          {/* Sensitivity tornado */}
          <div className={styles.tornadoSection}>
            <h3 className={styles.sectionTitle}>{t('simulation:sensitivityTitle')}<InfoTip text={t('simulation:tooltips.sensitivityTitle')} /></h3>
            <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 12 }}>
              {t('simulation:sensitivityDesc')}
            </p>
            <ResponsiveContainer width="100%" height={Math.max(200, result.sensitivity.length * 40 + 40)}>
              <BarChart
                layout="vertical"
                data={result.sensitivity}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid {...GRID_STYLE} horizontal={false} />
                <XAxis
                  type="number"
                  domain={[-1, 1]}
                  tickFormatter={(v: number) => `${(v * 100).toFixed(0)}%`}
                  tick={AXIS_STYLE}
                />
                <YAxis
                  type="category"
                  dataKey="label"
                  width={160}
                  tick={{ ...AXIS_STYLE, fontSize: 11 }}
                />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  formatter={(value: number) => [`${(value * 100).toFixed(1)}%`, t('simulation:correlation')]}
                  labelStyle={{ color: '#f1f5f9', fontWeight: 600, marginBottom: 4 }}
                />
                <ReferenceLine x={0} stroke="rgba(255,255,255,0.2)" />
                <Bar dataKey="correlation" radius={[4, 4, 4, 4]}>
                  {result.sensitivity.map((entry) => (
                    <Cell
                      key={entry.key}
                      fill={entry.correlation >= 0 ? '#6366f1' : '#fb7185'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
}
